import {
    CapabilityKey,
    CapabilityReason,
    PlanAccessLevel,
    gpCanUse,
    gpCanUseOnPlan,
} from './capabilities';

import type * as ng from 'angular';

/**
 * Asking the gate its two questions from inside the browser app, where the
 * answers depend on services that may or may not be registered.
 *
 * Shared by the `gp-capability` / `gp-if-withheld` directives and by
 * `gpCapabilityUpsell`, which must reach the SAME verdict: the directive
 * decides whether a control renders, the upsell service decides what happens
 * when it is used, and a control that renders live but whose action refuses —
 * or the reverse — is worse than either gate on its own.
 *
 * Everything here resolves through the injector rather than importing:
 * `gpPlanContent` and `gpPlanAccess` are registered on the workspace module,
 * and app-common must not depend on app-workspace. app-home and the reports app
 * legitimately have no plan model.
 */

/**
 * The verdict reason for `key`, narrowed by plan access when a plan is open.
 *
 * Falls back to the user-scoped check off the workspace — app-home has no plan,
 * and `gpCanUseOnPlan` can only ever SUBTRACT, so skipping it there withholds
 * nothing that should have been withheld.
 *
 * The level is never read from markup or passed by a caller. It is a fact about
 * the session, not about the call site, and an argument would be an untyped
 * number — the exact path PlanAccessLevel's boxing exists to close.
 */
export function capabilityReasonFor(
    $injector: ng.auto.IInjectorService, key: CapabilityKey): CapabilityReason {

    const level = planAccessLevel($injector);
    return level ? gpCanUseOnPlan(key, level).reason : gpCanUse(key).reason;
}

function planAccessLevel($injector: ng.auto.IInjectorService): PlanAccessLevel | null {
    if (!$injector.has('gpPlanAccess')) {
        return null;
    }

    try {
        return $injector.get<{ currentLevel(): PlanAccessLevel | null }>('gpPlanAccess').currentLevel();
    } catch {
        return null;
    }
}

/**
 * `isInPlan`, or false when nothing can answer it.
 *
 * False is the right answer with no plan open — with no plan there is no
 * existing content to preserve.
 */
export function planContainsCapability(
    $injector: ng.auto.IInjectorService, key: CapabilityKey): boolean {

    if (!$injector.has('gpPlanContent')) {
        return false;
    }

    try {
        return !!$injector.get<{ planContains(k: string): boolean }>('gpPlanContent').planContains(key);
    } catch {
        return false;
    }
}
